import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/*
 * ProductWarningText
 * A fixed-position tooltip that follows the cursor while hovering/focusing the trigger icon.
 * - Uses mousemove tracking only while visible (performance considerate).
 * - Accessible: trigger is a button with aria-describedby linking to tooltip content, supports keyboard focus.
 * - Falls back to original inline HTML if global window.LongHairWarning.text provided or product metafield value.
 */

const ProductWarningText = ({ product }) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ x: 0, y: 0 });
    const [fixedAt, setFixedAt] = useState(null);
    const tooltipRef = useRef(null);
    const triggerRef = useRef(null);
    const mouseMovedRef = useRef(false);
    const lastOpenWasKeyboard = useRef(false);
    const lastOpenWasTouch = useRef(false);

    const isLongHair = product?.tags?.some((tag) => tag === 'Show long hair warning');
    const longHairWarning = window.LongHairWarning?.text;
    const metaWarning = product?.productWarningValue?.value;
    const html = metaWarning ? `<p>${metaWarning}</p>` : longHairWarning;
    const shouldShow = !!product && (isLongHair || !!metaWarning) && !!html;

    const clampToViewport = useCallback((x, y) => {
        const margin = 8;
        const t = tooltipRef.current;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let width = 0;
        let height = 0;
        if (t) {
            const rect = t.getBoundingClientRect();
            width = rect.width;
            height = rect.height;
        } else {
            width = 240;
            height = 100;
        }
        let nx = x;
        let ny = y;
        if (nx + width + margin > vw) nx = vw - width - margin;
        if (ny + height + margin > vh) ny = vh - height - margin;
        if (nx < margin) nx = margin;
        if (ny < margin) ny = margin;
        return { x: nx, y: ny };
    }, []);

    // Global mouse tracking (instead of binding to trigger) so layout shifts don't desync cursor vs tooltip
    const handleGlobalMouseMove = useCallback(
        (e) => {
            if (fixedAt) return;
            mouseMovedRef.current = true;
            setCoords(clampToViewport(e.clientX + 12, e.clientY + 12));
        },
        [clampToViewport, fixedAt]
    );

    // Reposition relative to trigger (center + offset) – used for touch/pen, keyboard open, and layout shifts
    const repositionFromTrigger = useCallback(() => {
        const trg = triggerRef.current;
        if (!trg) return;
        const rect = trg.getBoundingClientRect();
        const anchored = clampToViewport(rect.left + rect.width / 2 + 12, rect.top + rect.height / 2 + 12);
        setCoords(anchored);
        setFixedAt(anchored); // maintain fixed anchor in touch/pen and keyboard scenarios
    }, [clampToViewport]);

    const open = useCallback(
        (reason = 'pointer', evt = null) => {
            if (visible) return;
            lastOpenWasKeyboard.current = reason === 'keyboard';
            lastOpenWasTouch.current = reason === 'touch';
            mouseMovedRef.current = false;
            if (reason === 'touch' || reason === 'keyboard') {
                repositionFromTrigger();
            } else if (reason === 'pointer' && evt) {
                // Seed initial position at pointer so first frame is correct before mousemove fires
                setCoords(clampToViewport(evt.clientX + 12, evt.clientY + 12));
            }
            setVisible(true);
        },
        [visible, repositionFromTrigger, clampToViewport]
    );

    const hide = useCallback(() => {
        setVisible(false);
        setFixedAt(null);
        lastOpenWasKeyboard.current = false;
        lastOpenWasTouch.current = false;
    }, []);

    // Re-clamp on resize/scroll while visible (keeps tooltip within viewport; does not re-anchor to trigger)
    useEffect(() => {
        if (!visible) return () => {};
        const handle = () => {
            setCoords((c) => clampToViewport(c.x, c.y));
        };
        window.addEventListener('resize', handle);
        window.addEventListener('scroll', handle, true);
        return () => {
            window.removeEventListener('resize', handle);
            window.removeEventListener('scroll', handle, true);
        };
    }, [visible, clampToViewport]);

    // Observe layout shifts affecting trigger position (e.g., container height growth) and re-anchor when appropriate.
    useEffect(() => {
        if (!visible) return () => {};
        const trg = triggerRef.current;
        if (!trg) return () => {};

        // IntersectionObserver fires when position relative to viewport changes (due to scroll or layout shift)
        const io = new IntersectionObserver((entries) => {
            if (!entries.length) return;
            // Re-anchor only in fixed/keyboard mode or before the user has moved the mouse.
            if (fixedAt || lastOpenWasKeyboard.current || !mouseMovedRef.current) {
                repositionFromTrigger();
            }
        });
        io.observe(trg);
        return () => io.disconnect();
    }, [visible, fixedAt, repositionFromTrigger]);

    // For keyboard open on fine pointer, ensure initial position before any mouse move.
    useEffect(() => {
        if (!visible) return;
        if (lastOpenWasKeyboard.current) {
            repositionFromTrigger();
        }
    }, [visible, repositionFromTrigger]);

    // Attach / detach window mousemove listener while visible (fine pointer only & not fixed/anchored)
    useEffect(() => {
        if (!visible) return () => {};
        if (fixedAt) return () => {};
        window.addEventListener('mousemove', handleGlobalMouseMove);
        return () => window.removeEventListener('mousemove', handleGlobalMouseMove);
    }, [visible, fixedAt, handleGlobalMouseMove]);

    // Escape key closes
    useEffect(() => {
        if (!visible) return () => {};
        const onKey = (e) => {
            if (e.key === 'Escape') hide();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [visible, hide]);

    // Outside tap closes when touch or pen opened the tooltip.
    useEffect(() => {
        if (!visible || !lastOpenWasTouch.current) return () => {};
        const handlePointerDown = (e) => {
            const trg = triggerRef.current;
            const tip = tooltipRef.current;
            if (!trg) return;
            if (trg.contains(e.target) || (tip && tip.contains(e.target))) return;
            hide();
        };
        // Use capture to run before potential focus shifts
        window.addEventListener('pointerdown', handlePointerDown, true);
        return () => window.removeEventListener('pointerdown', handlePointerDown, true);
    }, [visible, hide]);

    const tooltipId = `long-hair-warning-${product?.id?.toString().replace(/[^A-Za-z0-9_-]/g, '') || 'na'}`;

    if (!shouldShow) return null;

    return (
        <>
            <div className="long-hair-warning js-long-hair-warning">
                <button
                    type="button"
                    ref={triggerRef}
                    className="long-hair-warning__trigger"
                    aria-describedby={visible ? tooltipId : undefined}
                    aria-expanded={visible || undefined}
                    onPointerEnter={(e) => {
                        if (e.pointerType === 'mouse') open('pointer', e);
                    }}
                    onPointerLeave={(e) => {
                        if (e.pointerType === 'mouse') hide();
                    }}
                    onFocus={() => open('keyboard')}
                    onBlur={hide}
                    onPointerDown={(e) => {
                        if (e.pointerType === 'mouse') return;
                        e.preventDefault();
                        if (visible) hide();
                        else open('touch');
                    }}
                    onPointerUp={(e) => {
                        if (e.pointerType !== 'mouse') e.preventDefault();
                    }}
                    onClick={(e) => e.preventDefault()}
                >
                    <img
                        src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-warning.svg?v=1758017877"
                        alt="Warning"
                        aria-hidden="true"
                    />
                    <span className="visually-hidden">Product warning</span>
                </button>
            </div>

            {visible && html && (
                <div
                    id={tooltipId}
                    className="long-hair-warning__tooltip"
                    role="tooltip"
                    ref={tooltipRef}
                    style={{ left: `${coords.x}px`, top: `${coords.y}px` }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            )}
        </>
    );
};

export default ProductWarningText;

ProductWarningText.propTypes = {
    product: PropTypes.shape({
        tags: PropTypes.arrayOf(PropTypes.string),
        productWarningValue: PropTypes.shape({
            value: PropTypes.string,
        }),
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};
